import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

// ── Helpers ───────────────────────────────────────────────────────────────────
function diasNaEtapa(etapaDesdeParsed) {
  if (!etapaDesdeParsed) return 0;
  return (Date.now() - etapaDesdeParsed.getTime()) / (1000 * 60 * 60 * 24);
}

function corSemaforo(dias) {
  if (dias < 2)  return '#5BAD8F';
  if (dias < 3)  return '#C9A84C';
  return '#E05454';
}

function labelDias(dias) {
  const d = Math.floor(dias);
  if (d === 0) return 'hoje';
  if (d === 1) return '1d';
  return `${d}d`;
}

function labelEntrada(date) {
  if (!date) return '—';
  const agora    = new Date();
  const hojeStr  = agora.toISOString().split('T')[0];
  const dataStr  = date.toISOString().split('T')[0];
  const diffDias = Math.floor((agora - date) / 86400000);
  if (dataStr === hojeStr)  return 'hoje';
  if (diffDias < 7)         return 'esta semana';
  return date.toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit' });
}

// Gera cor consistente para cada projeto
function corProjeto(nome) {
  const CORES = ['#4A90D9','#9B7FD4','#5BAD8F','#D4789B','#C9A84C','#E05454','#0891b2','#E07B39'];
  if (!nome) return '#aaa';
  let hash = 0;
  for (let i = 0; i < nome.length; i++) hash = nome.charCodeAt(i) + ((hash << 5) - hash);
  return CORES[Math.abs(hash) % CORES.length];
}

function dbLeadToLocal(row) {
  const etapaDesde = row.etapa_desde
    ? new Date(row.etapa_desde)
    : row.created_at ? new Date(row.created_at) : new Date();
  return {
    id:               row.id,
    nome:             row.nome || '',
    telefone:         row.telefone || '',
    responsavel:      row.responsavel || '',
    funil:            row.funil_key || '',
    etapa:            row.etapa || '',
    prioridade:       row.prioridade || 'media',
    projeto:          row.projeto || '',
    obs:              row.obs || '',
    notas:            Array.isArray(row.notas) ? row.notas : [],
    etapaDesdeParsed: etapaDesde,
    createdAt:        row.created_at ? new Date(row.created_at) : new Date(),
    perdido:             row.perdido || false,
    transferidoDe:       row.transferido_de || null,
    status_qualificacao: row.status_qualificacao || 'em_analise',
    origem:              row.origem || null,
  };
}

const URL_REVISAO_CONTRATUAL = 'https://revisao-contratual-bancaria-production.up.railway.app';
const URL_SUPERENDIVIDAMENTO = 'https://calculadora-direito-bancario-production.up.railway.app';

const prioridadeConfig = {
  alta:  { cor: '#E05454', label: 'Alta' },
  media: { cor: '#C9A84C', label: 'Média' },
  baixa: { cor: '#5BAD8F', label: 'Baixa' },
};

export default function Leads() {
  const [leads, setLeads]         = useState([]);
  const [funis, setFunis]         = useState({});
  const [tarefas, setTarefas]     = useState([]);
  const [projetosMap, setProjetosMap] = useState({});
  const [projetosLista, setProjetosLista] = useState([]);
  const [colaboradores, setColaboradores] = useState([]);
  const [analisesRevisaoContratual, setAnalisesRevisaoContratual] = useState([]);
  const [analisesGestaoPassivo, setAnalisesGestaoPassivo] = useState([]);
  const [analisesSuperendividamento, setAnalisesSuperendividamento] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [leadAberto, setLeadAberto] = useState(null);
  const [editando, setEditando]   = useState(false);
  const [formEdit, setFormEdit]   = useState({});
  const [salvando, setSalvando]   = useState(false);
  const [novaObs, setNovaObs]     = useState('');
  const [salvandoObs, setSalvandoObs] = useState(false);
  const [filtros, setFiltros]     = useState({ busca: '', projeto: '', etapa: '', responsavel: '', funil: '', perdidos: '', qualificacao: '', origem: '' });

  useEffect(() => { carregarDados(); }, []);

  async function carregarDados() {
    setCarregando(true);
    // Busca todos os leads sem limite (lotes de 1000 até esgotar)
    async function buscarTodosLeads() {
      const todos = [];
      let pagina = 0;
      const lote = 1000;
      while (true) {
        const { data, error } = await supabase
          .from('crm_leads').select('*')
          .order('created_at', { ascending: false })
          .range(pagina * lote, (pagina + 1) * lote - 1);
        if (error || !data || data.length === 0) break;
        todos.push(...data);
        if (data.length < lote) break;
        pagina++;
      }
      return todos;
    }

    const [leadsDb, { data: funisDb }, { data: projDb }] = await Promise.all([
      buscarTodosLeads(),
      supabase.from('crm_funis').select('key, label, cor, etapas').order('created_at'),
      supabase.from('projetos').select('nome, categoria'),
    ]);

    const leadsLocais = leadsDb.map(dbLeadToLocal);
    setLeads(leadsLocais);

    const funisMap = {};
    (funisDb || []).forEach(f => { funisMap[f.key] = f; });
    setFunis(funisMap);

    const pm = {};
    (projDb || []).forEach(p => { pm[p.nome] = p.categoria || 'Outro'; });
    setProjetosMap(pm);
    setProjetosLista((projDb || []).map(p => p.nome).sort());

    const { data: colabDb } = await supabase.from('colaboradores').select('nome').ilike('status', 'ativo').order('nome');
    setColaboradores((colabDb || []).map(c => c.nome));

    // Carrega tarefas e análises de todos os leads — sem filtrar por .in('lead_id', ids):
    // com centenas de leads essa lista de ids gera uma URL de dezenas de milhares de
    // caracteres, que estoura o limite de headers HTTP (~16KB) e falha silenciosamente
    // (bug real encontrado em produção — o filtro por lead já é feito no lado do cliente
    // pelas funções tarefasDoLead/analisesXDoLead, então buscar tudo e filtrar depois é
    // seguro e resolve isso).
    if (leadsLocais.length > 0) {
      const { data: tarefasDb } = await supabase
        .from('crm_tarefas')
        .select('*')
        .order('data_hora');
      setTarefas(tarefasDb || []);

      // Tabelas novas (revisão contratual, gestão de passivo, superendividamento) — se
      // alguma ainda não existir no banco, falha silenciosamente e o selo/seção
      // correspondente simplesmente não aparece, sem quebrar o resto do CRM.
      const { data: analisesRC, error: erroRC } = await supabase
        .from('analises_revisao_contratual')
        .select('id, lead_id, nome_cliente, resultado, created_at')
        .order('created_at', { ascending: false });
      if (!erroRC) setAnalisesRevisaoContratual(analisesRC || []);

      const { data: analisesGP, error: erroGP } = await supabase
        .from('analises_gestao_passivo')
        .select('id, lead_id, nome_cliente, resultado, created_at')
        .order('created_at', { ascending: false });
      if (!erroGP) setAnalisesGestaoPassivo(analisesGP || []);

      const { data: analisesSE, error: erroSE } = await supabase
        .from('analises_superendividamento')
        .select('id, lead_id, nome_cliente, resultado, created_at')
        .order('created_at', { ascending: false });
      if (!erroSE) setAnalisesSuperendividamento(analisesSE || []);
    }

    setCarregando(false);
  }

  // ── Derivados ────────────────────────────────────────────────────────────
  const projetos       = [...new Set(leads.map(l => l.projeto).filter(Boolean))].sort();
  const responsaveis   = [...new Set(leads.map(l => l.responsavel).filter(Boolean))].sort();
  const etapasDisponiveis = [...new Set(leads.map(l => l.etapa).filter(Boolean))].sort();
  const funisDisponiveis  = Object.values(funis).map(f => ({ key: f.key, label: f.label }));

  const leadsFiltrados = leads.filter(l => {
    if (!filtros.perdidos && l.perdido)         return false;
    if (filtros.perdidos === 'apenas' && !l.perdido) return false;
    if (filtros.busca) {
      const q = filtros.busca.toLowerCase();
      if (!l.nome.toLowerCase().includes(q) && !l.telefone.includes(q)) return false;
    }
    if (filtros.projeto === '__sem_projeto__' && l.projeto) return false;
    if (filtros.projeto && filtros.projeto !== '__sem_projeto__' && l.projeto !== filtros.projeto) return false;
    if (filtros.etapa       && l.etapa       !== filtros.etapa)       return false;
    if (filtros.responsavel && l.responsavel !== filtros.responsavel) return false;
    if (filtros.funil       && l.funil       !== filtros.funil)       return false;
    if (filtros.qualificacao && l.status_qualificacao !== filtros.qualificacao) return false;
    if (filtros.origem       && l.origem       !== filtros.origem)       return false;
    return true;
  });

  function tarefasDoLead(leadId) {
    return tarefas.filter(t => t.lead_id === leadId);
  }

  function analisesRevisaoContratualDoLead(leadId) {
    return analisesRevisaoContratual.filter(a => a.lead_id === leadId);
  }

  function analisesGestaoPassivoDoLead(leadId) {
    return analisesGestaoPassivo.filter(a => a.lead_id === leadId);
  }

  function analisesSuperendividamentoDoLead(leadId) {
    return analisesSuperendividamento.filter(a => a.lead_id === leadId);
  }

  function corSinoPendente(leadId) {
    const pendentes = tarefasDoLead(leadId).filter(t => !t.concluida);
    if (pendentes.length === 0) return null;
    const agora = new Date();
    return pendentes.some(t => new Date(t.data_hora) <= agora) ? '#E07B39' : '#aaa';
  }

  // ── Ações ─────────────────────────────────────────────────────────────────
  async function adicionarNota() {
    if (!novaObs.trim() || !leadAberto) return;
    setSalvandoObs(true);
    const novaNota = { texto: novaObs.trim(), data_hora: new Date().toISOString() };
    const notasAtualizadas = [...(leadAberto.notas || []), novaNota];
    const { error } = await supabase.from('crm_leads').update({ notas: notasAtualizadas }).eq('id', leadAberto.id);
    if (!error) {
      const atualizado = { ...leadAberto, notas: notasAtualizadas };
      setLeadAberto(atualizado);
      setLeads(prev => prev.map(l => l.id === leadAberto.id ? atualizado : l));
      setNovaObs('');
    }
    setSalvandoObs(false);
  }

  async function marcarPerdido(lead) {
    const { error } = await supabase.from('crm_leads').update({ perdido: true }).eq('id', lead.id);
    if (!error) {
      const atualizado = { ...lead, perdido: true };
      setLeads(prev => prev.map(l => l.id === lead.id ? atualizado : l));
      setLeadAberto(atualizado);
    }
  }

  async function restaurarLead(lead) {
    const { error } = await supabase.from('crm_leads').update({ perdido: false }).eq('id', lead.id);
    if (!error) {
      const atualizado = { ...lead, perdido: false };
      setLeads(prev => prev.map(l => l.id === lead.id ? atualizado : l));
      setLeadAberto(atualizado);
    }
  }

  async function salvarProjeto(lead, novoProjeto) {
    const { error } = await supabase.from('crm_leads').update({ projeto: novoProjeto }).eq('id', lead.id);
    if (!error) {
      const atualizado = { ...lead, projeto: novoProjeto };
      setLeads(prev => prev.map(l => l.id === lead.id ? atualizado : l));
      setLeadAberto(atualizado);
    }
  }

  function abrirEdicao(lead) {
    setFormEdit({ nome: lead.nome, telefone: lead.telefone, responsavel: lead.responsavel, projeto: lead.projeto, prioridade: lead.prioridade, status_qualificacao: lead.status_qualificacao || 'em_analise', origem: lead.origem || '' });
    setEditando(true);
  }

  async function salvarEdicao() {
    if (!leadAberto) return;
    setSalvando(true);
    const { error } = await supabase.from('crm_leads').update({
      nome:                formEdit.nome,
      telefone:            formEdit.telefone,
      responsavel:         formEdit.responsavel,
      projeto:             formEdit.projeto,
      prioridade:          formEdit.prioridade,
      status_qualificacao: formEdit.status_qualificacao || 'em_analise',
      origem:              formEdit.origem || null,
    }).eq('id', leadAberto.id);
    if (!error) {
      const atualizado = { ...leadAberto, ...formEdit };
      setLeads(prev => prev.map(l => l.id === leadAberto.id ? atualizado : l));
      setLeadAberto(atualizado);
      setEditando(false);
    }
    setSalvando(false);
  }

  // ── Render ────────────────────────────────────────────────────────────────
  if (carregando) {
    return <div style={{ padding:40, textAlign:'center', color:'var(--gray-400)' }}>Carregando leads...</div>;
  }

  const totalAtivos  = leads.filter(l => !l.perdido).length;
  const totalPerdidos = leads.filter(l => l.perdido).length;

  return (
    <div style={{ position:'relative' }}>
      <div className="section-header">
        <h2 className="section-title">Leads</h2>
        <div className="section-line" />
      </div>

      {/* ── Métricas por área — escala para qualquer número de projetos ── */}
      {(() => {
        const ativos = leads.filter(l => !l.perdido);
        const semanaAtras = new Date(Date.now() - 7*86400000);

        // agrupa leads ativos por categoria do projeto
        const porArea = {};
        ativos.forEach(l => {
          const cat = projetosMap[l.projeto] || null;
          if (!cat) return;
          porArea[cat] = (porArea[cat] || 0) + 1;
        });

        const areaConfig = {
          'Advocacia':    { grad:'linear-gradient(135deg,#1e1254,#4338ca)', icon:'⚖️' },
          'Saúde':        { grad:'linear-gradient(135deg,#0c4a2a,#16a34a)', icon:'🩺' },
          'Infoprodutos': { grad:'linear-gradient(135deg,#431407,#ea580c)', icon:'📲' },
          'Outro':        { grad:'linear-gradient(135deg,#1f2937,#4b5563)', icon:'◈'  },
        };

        const metricas = [
          {
            label: 'Total ativos',
            valor: ativos.length,
            sub: `+${ativos.filter(l => l.createdAt >= semanaAtras).length} esta semana`,
            grad: 'linear-gradient(135deg,#3F0051,#7B2FBE)',
            icon: '✦',
          },
          {
            label: 'Perdidos',
            valor: totalPerdidos,
            sub: ativos.length > 0 ? `${((totalPerdidos/(ativos.length+totalPerdidos))*100).toFixed(0)}% do total` : '—',
            grad: 'linear-gradient(135deg,#7f1d1d,#dc2626)',
            icon: '✕',
          },
          ...Object.entries(porArea)
            .sort((a,b) => b[1]-a[1])
            .map(([cat, qtd]) => ({
              label: cat,
              valor: qtd,
              sub: `${((qtd/ativos.length)*100).toFixed(0)}% dos ativos`,
              grad: (areaConfig[cat] || areaConfig['Outro']).grad,
              icon: (areaConfig[cat] || areaConfig['Outro']).icon,
            })),
        ];

        return (
          <div style={{ display:'grid', gridTemplateColumns:`repeat(${Math.min(metricas.length, 5)}, 1fr)`, gap:10, marginBottom:22 }}>
            {metricas.map((m, i) => (
              <div key={i} style={{ background:m.grad, borderRadius:14, padding:'16px 20px', boxShadow:'0 4px 18px rgba(0,0,0,0.2)' }}>
                <div style={{ fontSize:9, color:'rgba(255,255,255,0.55)', textTransform:'uppercase', letterSpacing:'0.12em', fontWeight:700, marginBottom:8 }}>
                  {m.icon} {m.label}
                </div>
                <div style={{ fontSize:32, fontWeight:700, color:'white', fontFamily:'Playfair Display,serif', lineHeight:1 }}>{m.valor}</div>
                {m.sub && <div style={{ fontSize:9, color:'rgba(255,255,255,0.45)', marginTop:6, fontWeight:500 }}>{m.sub}</div>}
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── Filtros como chips elegantes ── */}
      <div style={{ display:'flex', gap:8, marginBottom:18, flexWrap:'wrap', alignItems:'center', background:'white', borderRadius:14, padding:'12px 16px', border:'1px solid rgba(107,33,168,0.1)', boxShadow:'0 2px 10px rgba(63,0,81,0.06)' }}>
        <input
          placeholder="🔍  Buscar nome ou telefone..."
          value={filtros.busca}
          onChange={e => setFiltros({...filtros, busca: e.target.value})}
          style={{ flex:2, minWidth:180, padding:'8px 14px', border:'1.5px solid rgba(107,33,168,0.12)', borderRadius:10, fontSize:12.5, background:'#FAF8FF', color:'#1E0A3C', outline:'none', fontFamily:'Poppins,sans-serif' }}
        />

        {projetos.length > 0 && (
          <select value={filtros.projeto} onChange={e => setFiltros({...filtros, projeto: e.target.value})}
            style={{ padding:'8px 14px', border:`1.5px solid ${filtros.projeto ? 'rgba(107,33,168,0.5)' : 'rgba(107,33,168,0.12)'}`, borderRadius:10, fontSize:12.5, background: filtros.projeto ? '#F3EBFF' : '#FAF8FF', color: filtros.projeto ? '#3F0051' : '#7B5EA7', cursor:'pointer', fontFamily:'Poppins,sans-serif', fontWeight: filtros.projeto ? 600 : 400 }}>
            <option value=''>Projeto</option>
            <option value='__sem_projeto__'>⚠ Sem projeto</option>
            {projetos.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        )}

        <select value={filtros.funil} onChange={e => setFiltros({...filtros, funil: e.target.value})}
          style={{ padding:'8px 14px', border:`1.5px solid ${filtros.funil ? 'rgba(107,33,168,0.5)' : 'rgba(107,33,168,0.12)'}`, borderRadius:10, fontSize:12.5, background: filtros.funil ? '#F3EBFF' : '#FAF8FF', color: filtros.funil ? '#3F0051' : '#7B5EA7', cursor:'pointer', fontFamily:'Poppins,sans-serif', fontWeight: filtros.funil ? 600 : 400 }}>
          <option value=''>Funil</option>
          {funisDisponiveis.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>

        <select value={filtros.etapa} onChange={e => setFiltros({...filtros, etapa: e.target.value})}
          style={{ padding:'8px 14px', border:`1.5px solid ${filtros.etapa ? 'rgba(107,33,168,0.5)' : 'rgba(107,33,168,0.12)'}`, borderRadius:10, fontSize:12.5, background: filtros.etapa ? '#F3EBFF' : '#FAF8FF', color: filtros.etapa ? '#3F0051' : '#7B5EA7', cursor:'pointer', fontFamily:'Poppins,sans-serif', fontWeight: filtros.etapa ? 600 : 400 }}>
          <option value=''>Etapa</option>
          {etapasDisponiveis.map(e => <option key={e} value={e}>{e}</option>)}
        </select>

        {responsaveis.length > 0 && (
          <select value={filtros.responsavel} onChange={e => setFiltros({...filtros, responsavel: e.target.value})}
            style={{ padding:'8px 14px', border:`1.5px solid ${filtros.responsavel ? 'rgba(107,33,168,0.5)' : 'rgba(107,33,168,0.12)'}`, borderRadius:10, fontSize:12.5, background: filtros.responsavel ? '#F3EBFF' : '#FAF8FF', color: filtros.responsavel ? '#3F0051' : '#7B5EA7', cursor:'pointer', fontFamily:'Poppins,sans-serif', fontWeight: filtros.responsavel ? 600 : 400 }}>
            <option value=''>Responsável</option>
            {responsaveis.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        )}

        <select value={filtros.perdidos} onChange={e => setFiltros({...filtros, perdidos: e.target.value})}
          style={{ padding:'8px 14px', border:`1.5px solid ${filtros.perdidos ? 'rgba(220,38,38,0.4)' : 'rgba(107,33,168,0.12)'}`, borderRadius:10, fontSize:12.5, background: filtros.perdidos ? '#fff0f0' : '#FAF8FF', color: filtros.perdidos ? '#dc2626' : '#7B5EA7', cursor:'pointer', fontFamily:'Poppins,sans-serif', fontWeight: filtros.perdidos ? 600 : 400 }}>
          <option value=''>Ativos</option>
          <option value='todos'>Todos</option>
          <option value='apenas'>Perdidos</option>
        </select>

        <select value={filtros.qualificacao} onChange={e => setFiltros({...filtros, qualificacao: e.target.value})}
          style={{ padding:'8px 14px', border:`1.5px solid ${filtros.qualificacao ? 'rgba(107,33,168,0.5)' : 'rgba(107,33,168,0.12)'}`, borderRadius:10, fontSize:12.5, background: filtros.qualificacao ? '#F3EBFF' : '#FAF8FF', color: filtros.qualificacao ? '#3F0051' : '#7B5EA7', cursor:'pointer', fontFamily:'Poppins,sans-serif', fontWeight: filtros.qualificacao ? 600 : 400 }}>
          <option value=''>Qualificação</option>
          <option value='qualificado'>✅ Qualificado</option>
          <option value='em_analise'>🔍 Em análise</option>
          <option value='desqualificado'>❌ Desqualificado</option>
        </select>

        <select value={filtros.origem} onChange={e => setFiltros({...filtros, origem: e.target.value})}
          style={{ padding:'8px 14px', border:`1.5px solid ${filtros.origem ? 'rgba(107,33,168,0.5)' : 'rgba(107,33,168,0.12)'}`, borderRadius:10, fontSize:12.5, background: filtros.origem ? '#F3EBFF' : '#FAF8FF', color: filtros.origem ? '#3F0051' : '#7B5EA7', cursor:'pointer', fontFamily:'Poppins,sans-serif', fontWeight: filtros.origem ? 600 : 400 }}>
          <option value=''>Origem</option>
          <option value='instagram'>📸 Instagram</option>
          <option value='tiktok'>🎵 TikTok</option>
          <option value='trafego'>📢 Tráfego</option>
          <option value='indicacao'>🤝 Indicação</option>
        </select>

        {(filtros.busca || filtros.projeto || filtros.funil || filtros.etapa || filtros.responsavel || filtros.perdidos || filtros.qualificacao || filtros.origem) && (
          <button onClick={() => setFiltros({ busca:'', projeto:'', etapa:'', responsavel:'', funil:'', perdidos:'', qualificacao:'', origem:'' })}
            style={{ padding:'8px 12px', border:'none', borderRadius:10, background:'#F3EBFF', color:'#7B2FBE', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'Poppins,sans-serif' }}>
            ✕ Limpar
          </button>
        )}

        <span style={{ marginLeft:'auto', fontSize:12, color:'#7B5EA7', fontWeight:600, background:'#F3EBFF', padding:'4px 12px', borderRadius:20 }}>
          {leadsFiltrados.length} leads
        </span>
      </div>

      {/* ── Lista de leads — cards bonitos ── */}
      <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
        {leadsFiltrados.map((lead) => {
          const dias      = diasNaEtapa(lead.etapaDesdeParsed);
          const semaf     = corSemaforo(dias);
          const funilInfo = funis[lead.funil];
          const sinoCor   = corSinoPendente(lead.id);
          const analisesLead = analisesRevisaoContratualDoLead(lead.id);
          const totalTeses = analisesLead.reduce((soma, a) => soma + (a.resultado?.matrizTeses?.length || 0), 0);
          const analisesGPLead = analisesGestaoPassivoDoLead(lead.id);
          const analisesSELead = analisesSuperendividamentoDoLead(lead.id);
          const projCor   = corProjeto(lead.projeto);
          const iniciais  = lead.nome ? lead.nome.split(' ').map(p=>p[0]).slice(0,2).join('').toUpperCase() : '?';
          return (
            <div key={lead.id}
              onClick={() => { setLeadAberto(lead); setNovaObs(''); }}
              style={{
                background: lead.perdido ? 'linear-gradient(135deg,#fff8f8,#fff0f0)' : 'white',
                borderRadius:14, padding:'14px 18px',
                border:`1px solid ${lead.perdido ? 'rgba(220,38,38,0.15)' : 'rgba(107,33,168,0.08)'}`,
                boxShadow:'0 2px 8px rgba(63,0,81,0.06)',
                cursor:'pointer', transition:'all 0.18s',
                display:'flex', alignItems:'center', gap:14,
                borderLeft:`4px solid ${semaf}`,
              }}
              onMouseEnter={e => { e.currentTarget.style.transform='translateY(-1px)'; e.currentTarget.style.boxShadow='0 6px 20px rgba(63,0,81,0.12)'; }}
              onMouseLeave={e => { e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 2px 8px rgba(63,0,81,0.06)'; }}
            >
              {/* Avatar */}
              <div style={{ width:40, height:40, borderRadius:12, background:`linear-gradient(135deg,${projCor || '#7B2FBE'},${projCor || '#7B2FBE'}88)`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:700, color:'white', flexShrink:0, boxShadow:`0 3px 10px ${projCor || '#7B2FBE'}44` }}>
                {iniciais}
              </div>

              {/* Nome + telefone */}
              <div style={{ flex:2, minWidth:0 }}>
                <div style={{ fontWeight:600, color:'#1E0A3C', fontSize:13.5, lineHeight:1.3 }}>{lead.nome}</div>
                <div style={{ fontSize:11, color:'#7B5EA7', marginTop:2 }}>{lead.telefone}</div>
              </div>

              {/* Projeto */}
              <div style={{ flex:1.5, minWidth:100 }}>
                {lead.projeto
                  ? <span style={{ fontSize:11, fontWeight:700, color:projCor, background:`${projCor}15`, padding:'4px 10px', borderRadius:20, whiteSpace:'nowrap', border:`1px solid ${projCor}30` }}>{lead.projeto}</span>
                  : <span style={{ fontSize:11, color:'#C084FC', fontStyle:'italic' }}>sem projeto</span>}
              </div>

              {/* Funil · Etapa */}
              <div style={{ flex:1.5, minWidth:110 }}>
                <div style={{ fontSize:12, color: funilInfo?.cor || '#7B5EA7', fontWeight:600 }}>{funilInfo?.label || lead.funil}</div>
                <div style={{ fontSize:11, color:'#7B5EA7', marginTop:2 }}>{lead.etapa}</div>
              </div>

              {/* Responsável */}
              <div style={{ flex:1, minWidth:80, fontSize:12.5, color:'#1E0A3C', fontWeight:500 }}>
                {lead.responsavel || <span style={{ color:'#C084FC', fontSize:11 }}>—</span>}
              </div>

              {/* Selos de análises vinculadas (revisão contratual, gestão de passivo, superendividamento) */}
              {(analisesLead.length > 0 || analisesGPLead.length > 0 || analisesSELead.length > 0) && (
                <div style={{ flexShrink:0, display:'flex', gap:6 }}>
                  {analisesLead.length > 0 && (
                    <span title={`${analisesLead.length} análise(s) de revisão contratual`} style={{ fontSize:11, fontWeight:700, color:'#7B2FBE', background:'#F3EBFF', padding:'4px 10px', borderRadius:20, whiteSpace:'nowrap', border:'1px solid #7B2FBE30' }}>
                      ⚖️ {totalTeses > 0 ? `${totalTeses} tese(s)` : `${analisesLead.length} análise(s)`}
                    </span>
                  )}
                  {analisesGPLead.length > 0 && (
                    <span title={`${analisesGPLead.length} análise(s) de gestão de passivo`} style={{ fontSize:11, fontWeight:700, color:'#0F766E', background:'#ECFDF5', padding:'4px 10px', borderRadius:20, whiteSpace:'nowrap', border:'1px solid #0F766E30' }}>
                      🏢 {analisesGPLead.length} análise(s)
                    </span>
                  )}
                  {analisesSELead.length > 0 && (
                    <span title={`${analisesSELead.length} análise(s) de superendividamento`} style={{ fontSize:11, fontWeight:700, color:'#B45309', background:'#FFFBEB', padding:'4px 10px', borderRadius:20, whiteSpace:'nowrap', border:'1px solid #B4530930' }}>
                      💳 {analisesSELead.length} análise(s)
                    </span>
                  )}
                </div>
              )}

              {/* Bloco de tempo: entrada + tempo na etapa */}
              {(() => {
                const lbl      = labelEntrada(lead.createdAt);
                const isHoje   = lbl === 'hoje';
                const isSemana = lbl === 'esta semana';
                const corEntrada = isHoje ? '#059669' : isSemana ? '#7B2FBE' : '#6b7280';
                return (
                  <div style={{ textAlign:'right', flexShrink:0, minWidth:96 }}>
                    {/* Entrada do lead — informação principal */}
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:5 }}>
                      <div style={{ width:7, height:7, borderRadius:'50%', background:corEntrada, flexShrink:0 }} />
                      <span style={{ fontSize:12, fontWeight:700, color:corEntrada }}>{lbl}</span>
                      {sinoCor && <span style={{ fontSize:12 }}>🔔</span>}
                    </div>
                    {/* Tempo na etapa atual — controle secundário */}
                    <div style={{ display:'flex', alignItems:'center', justifyContent:'flex-end', gap:4, marginTop:4 }}>
                      <div style={{ width:6, height:6, borderRadius:'50%', background:semaf, flexShrink:0 }} />
                      <span style={{ fontSize:10, color:semaf, fontWeight:600 }}>{labelDias(dias)} nesta etapa</span>
                    </div>
                    {lead.perdido && <div style={{ fontSize:9, background:'#fee2e2', color:'#dc2626', padding:'1px 7px', borderRadius:10, fontWeight:700, marginTop:4, display:'inline-block' }}>Perdido</div>}
                  </div>
                );
              })()}
            </div>
          );
        })}

        {leadsFiltrados.length === 0 && (
          <div style={{ textAlign:'center', padding:60, color:'#7B5EA7', background:'white', borderRadius:16, border:'1px solid rgba(107,33,168,0.08)' }}>
            <div style={{ fontSize:40, marginBottom:12, opacity:0.3 }}>✦</div>
            <div style={{ fontSize:15, fontWeight:600, fontFamily:'Playfair Display,serif', color:'#3F0051', marginBottom:6 }}>
              {leads.length === 0 ? 'Nenhum lead ainda' : 'Nenhum resultado'}
            </div>
            <div style={{ fontSize:12, color:'#C084FC' }}>
              {leads.length === 0 ? 'Adicione leads pelo menu CRM.' : 'Tente ajustar os filtros.'}
            </div>
          </div>
        )}
      </div>

      {/* Painel lateral do lead */}
      {leadAberto && (
        <div style={{ position:'fixed', inset:0, zIndex:1000, display:'flex' }}>
          <div style={{ flex:1, background:'rgba(0,0,0,0.4)' }} onClick={() => setLeadAberto(null)} />
          <div style={{ width:540, background:'var(--card-bg, #fff)', borderLeft:'1px solid var(--border)', overflowY:'auto', padding:28, boxShadow:'-4px 0 24px rgba(0,0,0,0.15)' }}>

            {/* Header */}
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:20 }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:4 }}>
                  <h2 style={{ margin:0, fontSize:18, color:'var(--text-primary)', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{leadAberto.nome || <span style={{ color:'#C084FC', fontStyle:'italic' }}>Sem nome</span>}</h2>
                  {leadAberto.perdido && <span style={{ fontSize:11, background:'#fee2e2', color:'#E05454', padding:'2px 8px', borderRadius:20, fontWeight:700, flexShrink:0 }}>Perdido</span>}
                </div>
                <div style={{ fontSize:11, color:'#7B5EA7' }}>{leadAberto.telefone || '—'}</div>
              </div>
              <div style={{ display:'flex', gap:8, flexShrink:0 }}>
                {!editando && (
                  <button onClick={() => abrirEdicao(leadAberto)}
                    style={{ padding:'7px 14px', borderRadius:9, border:'1.5px solid rgba(107,33,168,0.2)', background:'white', color:'#7B2FBE', cursor:'pointer', fontSize:12, fontWeight:600, fontFamily:'Poppins,sans-serif' }}>
                    ✏ Editar
                  </button>
                )}
                <button onClick={() => { setLeadAberto(null); setEditando(false); }}
                  style={{ background:'transparent', border:'none', fontSize:22, cursor:'pointer', color:'var(--gray-400)', lineHeight:1 }}>×</button>
              </div>
            </div>

            {/* Modo edição */}
            {editando && (
              <div style={{ background:'#FAF8FF', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:14, padding:18, marginBottom:16 }}>
                <div style={{ fontSize:12, fontWeight:700, color:'#7B2FBE', marginBottom:14, textTransform:'uppercase', letterSpacing:'0.06em' }}>✏ Editar dados do lead</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                  {[
                    { label:'Nome',     key:'nome',      type:'text' },
                    { label:'Telefone', key:'telefone',  type:'text' },
                  ].map(f => (
                    <div key={f.key}>
                      <div style={{ fontSize:10, color:'#7B5EA7', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>{f.label}</div>
                      <input value={formEdit[f.key] || ''} onChange={e => setFormEdit(p => ({ ...p, [f.key]: e.target.value }))}
                        style={{ width:'100%', padding:'8px 12px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:8, fontSize:13, fontFamily:'Poppins,sans-serif', color:'#1E0A3C', background:'white', boxSizing:'border-box' }} />
                    </div>
                  ))}
                  <div>
                    <div style={{ fontSize:10, color:'#7B5EA7', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Projeto</div>
                    <select value={formEdit.projeto || ''} onChange={e => setFormEdit(p => ({ ...p, projeto: e.target.value }))}
                      style={{ width:'100%', padding:'8px 12px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:8, fontSize:13, fontFamily:'Poppins,sans-serif', color:'#1E0A3C', background:'white' }}>
                      <option value=''>— Sem projeto —</option>
                      {projetosLista.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:'#7B5EA7', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Responsável</div>
                    <select value={formEdit.responsavel || ''} onChange={e => setFormEdit(p => ({ ...p, responsavel: e.target.value }))}
                      style={{ width:'100%', padding:'8px 12px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:8, fontSize:13, fontFamily:'Poppins,sans-serif', color:'#1E0A3C', background:'white' }}>
                      <option value=''>— Sem responsável —</option>
                      {colaboradores.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:'#7B5EA7', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Prioridade</div>
                    <select value={formEdit.prioridade || 'media'} onChange={e => setFormEdit(p => ({ ...p, prioridade: e.target.value }))}
                      style={{ width:'100%', padding:'8px 12px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:8, fontSize:13, fontFamily:'Poppins,sans-serif', color:'#1E0A3C', background:'white' }}>
                      <option value='alta'>Alta</option>
                      <option value='media'>Média</option>
                      <option value='baixa'>Baixa</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:'#7B5EA7', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Origem</div>
                    <select value={formEdit.origem || ''} onChange={e => setFormEdit(p => ({ ...p, origem: e.target.value }))}
                      style={{ width:'100%', padding:'8px 12px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:8, fontSize:13, fontFamily:'Poppins,sans-serif', color:'#1E0A3C', background:'white' }}>
                      <option value=''>— Não informado —</option>
                      <option value='instagram'>📸 Instagram</option>
                      <option value='tiktok'>🎵 TikTok</option>
                      <option value='trafego'>📢 Tráfego</option>
                      <option value='indicacao'>🤝 Indicação</option>
                    </select>
                  </div>
                  <div>
                    <div style={{ fontSize:10, color:'#7B5EA7', fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Qualificação</div>
                    <select value={formEdit.status_qualificacao || 'em_analise'} onChange={e => setFormEdit(p => ({ ...p, status_qualificacao: e.target.value }))}
                      style={{ width:'100%', padding:'8px 12px', border:'1.5px solid rgba(107,33,168,0.15)', borderRadius:8, fontSize:13, fontFamily:'Poppins,sans-serif', color:'#1E0A3C', background:'white' }}>
                      <option value='qualificado'>✅ Qualificado</option>
                      <option value='em_analise'>🔍 Em análise</option>
                      <option value='desqualificado'>❌ Desqualificado</option>
                    </select>
                  </div>
                </div>
                <div style={{ display:'flex', gap:8, marginTop:14 }}>
                  <button onClick={salvarEdicao} disabled={salvando}
                    style={{ flex:1, padding:'9px 0', border:'none', borderRadius:9, background:'linear-gradient(135deg,#3F0051,#7B2FBE)', color:'white', fontWeight:700, cursor:'pointer', fontSize:13, fontFamily:'Poppins,sans-serif', opacity: salvando ? 0.7 : 1 }}>
                    {salvando ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                  <button onClick={() => setEditando(false)}
                    style={{ padding:'9px 18px', border:'1.5px solid rgba(107,33,168,0.2)', borderRadius:9, background:'white', color:'#7B5EA7', fontWeight:600, cursor:'pointer', fontSize:13, fontFamily:'Poppins,sans-serif' }}>
                    Cancelar
                  </button>
                </div>
              </div>
            )}

            {/* Semáforo */}
            {(() => {
              const dias  = diasNaEtapa(leadAberto.etapaDesdeParsed);
              const semaf = corSemaforo(dias);
              return (
                <div style={{ display:'flex', alignItems:'center', gap:8, padding:'8px 14px', background:`${semaf}11`, border:`1px solid ${semaf}44`, borderRadius:8, marginBottom:16 }}>
                  <div style={{ width:10, height:10, borderRadius:'50%', background:semaf }} />
                  <span style={{ fontSize:12, fontWeight:700, color:semaf }}>{labelDias(dias)} na etapa "{leadAberto.etapa}"</span>
                  {dias >= 3 && <span style={{ fontSize:11, color:semaf }}>— Vencido</span>}
                </div>
              );
            })()}

            {/* Dados (somente leitura quando não editando) */}
            {!editando && (
            <div className="card" style={{ padding:16, marginBottom:14 }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                {[
                  { label:'Telefone',    val: leadAberto.telefone },
                  { label:'Responsável', val: leadAberto.responsavel },
                  { label:'Funil',       val: funis[leadAberto.funil]?.label || leadAberto.funil },
                  { label:'Etapa',       val: leadAberto.etapa },
                  { label:'Prioridade',     val: prioridadeConfig[leadAberto.prioridade]?.label },
                  { label:'Entrada',        val: leadAberto.createdAt.toLocaleDateString('pt-BR') },
                  { label:'Projeto',        val: leadAberto.projeto },
                  { label:'Origem',         val: leadAberto.origem ? { instagram:'📸 Instagram', tiktok:'🎵 TikTok', trafego:'📢 Tráfego', indicacao:'🤝 Indicação' }[leadAberto.origem] : null },
                  { label:'Qualificação',   val: { qualificado:'✅ Qualificado', em_analise:'🔍 Em análise', desqualificado:'❌ Desqualificado' }[leadAberto.status_qualificacao] },
                ].map(f => (
                  <div key={f.label}>
                    <div style={{ fontSize:10, color:'var(--gray-400)', textTransform:'uppercase', fontWeight:600, marginBottom:3 }}>{f.label}</div>
                    <div style={{ fontSize:13, color:'var(--text-primary)', fontWeight:500 }}>{f.val || '—'}</div>
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Análises de revisão contratual bancária vinculadas */}
            {analisesRevisaoContratualDoLead(leadAberto.id).length > 0 && (
              <div className="card" style={{ padding:16, marginBottom:14, border:'1px solid #7B2FBE30', background:'#FBF7FF' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#7B2FBE', textTransform:'uppercase', marginBottom:10 }}>⚖️ Revisão contratual bancária</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {analisesRevisaoContratualDoLead(leadAberto.id).map(a => {
                    const nTeses = a.resultado?.matrizTeses?.length || 0;
                    const nCaminhos = a.resultado?.caminhosNaoJudiciais?.length || 0;
                    return (
                      <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, background:'white', borderRadius:10, padding:'10px 12px', border:'1px solid rgba(107,33,168,0.1)' }}>
                        <div>
                          <div style={{ fontSize:12.5, fontWeight:600, color:'#1E0A3C' }}>
                            {nTeses > 0 ? `${nTeses} tese(s) identificada(s)` : 'Nenhuma tese identificada'}
                            {nCaminhos > 0 && ` · ${nCaminhos} caminho(s) não-judicial(is)`}
                          </div>
                          <div style={{ fontSize:11, color:'#7B5EA7', marginTop:2 }}>
                            {new Date(a.created_at).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric' })}
                          </div>
                        </div>
                        <a href={`${URL_REVISAO_CONTRATUAL}/revisao-contratual/salvo/${a.id}`} target="_blank" rel="noreferrer"
                          style={{ fontSize:11.5, fontWeight:700, color:'#7B2FBE', background:'#F3EBFF', padding:'6px 12px', borderRadius:8, whiteSpace:'nowrap', textDecoration:'none' }}>
                          Ver relatório →
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Análises de gestão de passivo empresarial vinculadas */}
            {analisesGestaoPassivoDoLead(leadAberto.id).length > 0 && (
              <div className="card" style={{ padding:16, marginBottom:14, border:'1px solid #0F766E30', background:'#F0FDF9' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#0F766E', textTransform:'uppercase', marginBottom:10 }}>🏢 Gestão de passivo empresarial</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {analisesGestaoPassivoDoLead(leadAberto.id).map(a => {
                    const LABEL_CAMINHO = {
                      negociacao_extrajudicial_simples: 'Negociação extrajudicial simples',
                      recuperacao_extrajudicial: 'Recuperação extrajudicial',
                      recuperacao_judicial: 'Recuperação judicial',
                      nao_elegivel_avaliar_alternativas: 'Não elegível — avaliar alternativas',
                    };
                    const caminho = LABEL_CAMINHO[a.resultado?.recomendacao?.caminho] || 'Recomendação não identificada';
                    return (
                      <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, background:'white', borderRadius:10, padding:'10px 12px', border:'1px solid rgba(15,118,110,0.1)' }}>
                        <div>
                          <div style={{ fontSize:12.5, fontWeight:600, color:'#1E0A3C' }}>{caminho}</div>
                          <div style={{ fontSize:11, color:'#7B5EA7', marginTop:2 }}>
                            {new Date(a.created_at).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric' })}
                          </div>
                        </div>
                        <a href={`${URL_REVISAO_CONTRATUAL}/gestao-passivo/salvo/${a.id}`} target="_blank" rel="noreferrer"
                          style={{ fontSize:11.5, fontWeight:700, color:'#0F766E', background:'#ECFDF5', padding:'6px 12px', borderRadius:8, whiteSpace:'nowrap', textDecoration:'none' }}>
                          Ver relatório →
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Análises de superendividamento vinculadas */}
            {analisesSuperendividamentoDoLead(leadAberto.id).length > 0 && (
              <div className="card" style={{ padding:16, marginBottom:14, border:'1px solid #B4530930', background:'#FFFDF5' }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#B45309', textTransform:'uppercase', marginBottom:10 }}>💳 Superendividamento</div>
                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                  {analisesSuperendividamentoDoLead(leadAberto.id).map(a => {
                    const enquadrado = a.resultado?.conclusao?.enquadramento === 'enquadrado';
                    return (
                      <div key={a.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:10, background:'white', borderRadius:10, padding:'10px 12px', border:'1px solid rgba(180,83,9,0.1)' }}>
                        <div>
                          <div style={{ fontSize:12.5, fontWeight:600, color:'#1E0A3C' }}>
                            {a.resultado?.conclusao?.titulo || (enquadrado ? 'Enquadrado em superendividamento' : 'Não enquadrado')}
                          </div>
                          <div style={{ fontSize:11, color:'#7B5EA7', marginTop:2 }}>
                            {new Date(a.created_at).toLocaleDateString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric' })}
                          </div>
                        </div>
                        <a href={`${URL_SUPERENDIVIDAMENTO}/superendividamento/salvo/${a.id}`} target="_blank" rel="noreferrer"
                          style={{ fontSize:11.5, fontWeight:700, color:'#B45309', background:'#FFFBEB', padding:'6px 12px', borderRadius:8, whiteSpace:'nowrap', textDecoration:'none' }}>
                          Ver relatório →
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Tarefas pendentes */}
            {(() => {
              const agora = new Date();
              const tdLead = tarefasDoLead(leadAberto.id).filter(t => !t.concluida);
              if (tdLead.length === 0) return null;
              return (
                <div className="card" style={{ padding:14, marginBottom:14, border:'1px solid #E07B3944', background:'#fff8f4' }}>
                  <div style={{ fontSize:11, fontWeight:700, color:'#E07B39', textTransform:'uppercase', marginBottom:8 }}>🔔 Lembretes pendentes</div>
                  {tdLead.map(t => {
                    const dt = new Date(t.data_hora);
                    const vencida = dt <= agora;
                    return (
                      <div key={t.id} style={{ fontSize:13, color: vencida ? '#E07B39' : 'var(--text-primary)', marginBottom:4, fontWeight: vencida ? 600 : 400 }}>
                        {t.titulo} — {dt.toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' })}
                        {vencida && ' ⚠ Vencido'}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* Log de observações */}
            <div className="card" style={{ padding:16, marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:'var(--gray-400)', textTransform:'uppercase', marginBottom:12 }}>Observações</div>

              {(leadAberto.notas || []).length > 0 && (
                <div style={{ maxHeight:200, overflowY:'auto', marginBottom:12, display:'flex', flexDirection:'column', gap:8 }}>
                  {[...(leadAberto.notas || [])].reverse().map((nota, i) => (
                    <div key={i} style={{ background:'var(--bg-secondary)', borderRadius:7, padding:'8px 12px' }}>
                      <div style={{ fontSize:10, color:'var(--gray-400)', marginBottom:3 }}>
                        {new Date(nota.data_hora).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                      </div>
                      <div style={{ fontSize:13, color:'var(--text-primary)', whiteSpace:'pre-wrap' }}>{nota.texto}</div>
                    </div>
                  ))}
                  {leadAberto.obs && (
                    <div style={{ background:'var(--bg-secondary)', borderRadius:7, padding:'8px 12px', opacity:0.6, border:'1px dashed var(--border)' }}>
                      <div style={{ fontSize:10, color:'var(--gray-400)', marginBottom:3 }}>obs. importada</div>
                      <div style={{ fontSize:13, color:'var(--text-primary)' }}>{leadAberto.obs}</div>
                    </div>
                  )}
                </div>
              )}

              {(leadAberto.notas || []).length === 0 && leadAberto.obs && (
                <div style={{ marginBottom:12, background:'var(--bg-secondary)', borderRadius:7, padding:'8px 12px', opacity:0.7 }}>
                  <div style={{ fontSize:10, color:'var(--gray-400)', marginBottom:3 }}>obs. importada</div>
                  <div style={{ fontSize:13, color:'var(--text-primary)' }}>{leadAberto.obs}</div>
                </div>
              )}

              {(leadAberto.notas || []).length === 0 && !leadAberto.obs && (
                <div style={{ fontSize:12, color:'var(--gray-400)', marginBottom:12 }}>Nenhuma observação ainda.</div>
              )}

              <div style={{ display:'flex', gap:8 }}>
                <textarea
                  value={novaObs}
                  onChange={e => setNovaObs(e.target.value)}
                  placeholder="Nova observação..."
                  rows={2}
                  style={{ flex:1, padding:'8px 10px', border:'1px solid var(--border)', borderRadius:6, fontSize:13, background:'var(--bg-secondary)', color:'var(--text-primary)', resize:'vertical', boxSizing:'border-box' }}
                />
                <button onClick={adicionarNota} disabled={!novaObs.trim() || salvandoObs}
                  style={{ alignSelf:'flex-end', padding:'8px 14px', border:'none', borderRadius:6, background:'var(--purple)', color:'#fff', cursor:'pointer', fontWeight:600, fontSize:12, opacity: !novaObs.trim() ? 0.5 : 1 }}>
                  {salvandoObs ? '...' : 'Salvar'}
                </button>
              </div>
            </div>

            {/* Ações */}
            <div style={{ display:'flex', gap:10 }}>
              {!leadAberto.perdido ? (
                <button onClick={() => marcarPerdido(leadAberto)}
                  style={{ flex:1, background:'#fff5f5', color:'#E05454', border:'2px solid #E05454', padding:'10px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:13 }}>
                  Negócio perdido
                </button>
              ) : (
                <button onClick={() => restaurarLead(leadAberto)}
                  style={{ flex:1, background:'#f0fff8', color:'#5BAD8F', border:'2px solid #5BAD8F', padding:'10px', borderRadius:7, fontWeight:700, cursor:'pointer', fontSize:13 }}>
                  Reativar lead
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
